// Agendar pela tela (pedido 2026-10-21): título, data no calendário
// completo (com ano), horário no relógio de rolar, descrição opcional e
// lembrete. Serve pra novo e pra editar. O servidor manda a confirmação
// (push/WhatsApp) e agenda o lembrete, igual quando a IA anota.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarDays, Clock, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { gpsDriveAiAssistantService, type AiPersonalEvent } from "@/services/gpsdrive-ai-assistant.service";
import { CalendarPicker } from "./calendar-picker";
import { TimeWheel } from "./time-wheel";

const TZ = "America/Sao_Paulo";
const REMINDERS: { label: string; value: number | null }[] = [
  { label: "Sem lembrete", value: null },
  { label: "Na hora", value: 0 },
  { label: "10 min antes", value: 10 },
  { label: "30 min antes", value: 30 },
  { label: "1 h antes", value: 60 },
  { label: "1 dia antes", value: 1440 },
];

const longDay = (ymd: string) => {
  const s = new Date(`${ymd}T12:00:00Z`).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Próximo horário "redondo" (de 30 em 30 min) pra um novo compromisso.
function defaultTime() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: TZ }));
  const mins = now.getHours() * 60 + now.getMinutes();
  const next = Math.min(23 * 60 + 30, Math.ceil((mins + 1) / 30) * 30);
  return `${String(Math.floor(next / 60)).padStart(2, "0")}:${String(next % 60).padStart(2, "0")}`;
}

export function EventEditor({
  event,
  initialDate,
  today,
  marked,
  onClose,
  onSaved,
}: {
  event?: AiPersonalEvent | null;
  initialDate: string;
  today: string;
  marked?: Set<string>;
  onClose: () => void;
  onSaved?: (date: string) => void;
}) {
  const qc = useQueryClient();
  const editing = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [notes, setNotes] = useState(event?.notes ?? "");
  const [date, setDate] = useState(event ? new Date(event.startsAt).toLocaleDateString("en-CA", { timeZone: TZ }) : initialDate);
  const [time, setTime] = useState(
    event ? new Date(event.startsAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ }) : defaultTime(),
  );
  const [remind, setRemind] = useState<number | null>(event ? event.remindMinutesBefore : 30);
  const [panel, setPanel] = useState<"none" | "date" | "time">("none");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const input = { title: title.trim(), notes: notes.trim(), date, time, remindMinutesBefore: remind };
      return editing ? gpsDriveAiAssistantService.updateEvent(event!.id, input) : gpsDriveAiAssistantService.createEvent(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-assistant", "events"] });
      onSaved?.(date);
      onClose();
    },
    onError: (e) => setError(e instanceof GpsDriveApiError || e instanceof Error ? e.message : "Não foi possível salvar"),
  });

  const past = new Date(`${date}T${time}:00-03:00`).getTime() < Date.now();

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-black/40" onClick={onClose}>
      <div
        className="ai-in mt-auto flex max-h-[92%] flex-col overflow-hidden rounded-t-[28px] bg-white dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <button type="button" aria-label="Fechar" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
          <h3 className="flex-1 text-base font-extrabold text-slate-900 dark:text-white">{editing ? "Editar compromisso" : "Novo compromisso"}</h3>
          <button
            type="button"
            disabled={!title.trim() || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            className="rounded-full ai-grad px-5 py-2 text-sm font-bold shadow-md transition active:scale-95 disabled:opacity-50"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
          <input
            autoFocus={!editing}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Adicionar título"
            className="w-full border-b-2 ai-border bg-transparent pb-2 text-2xl font-bold text-slate-900 outline-none placeholder:text-slate-300 focus:border-[var(--ai)] dark:text-white"
          />

          {/* Data */}
          <button
            type="button"
            onClick={() => setPanel(panel === "date" ? "none" : "date")}
            className={cn("flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition", panel === "date" ? "ai-soft" : "bg-slate-50 dark:bg-slate-800/60")}
          >
            <CalendarDays className="h-5 w-5 ai-text" />
            <span className="flex-1 font-semibold text-slate-800 dark:text-slate-100">{longDay(date)}</span>
          </button>
          {panel === "date" && (
            <div className="rounded-3xl border ai-border p-3">
              <CalendarPicker
                value={date}
                today={today}
                marked={marked}
                onChange={(d) => {
                  setDate(d);
                  setPanel("time");
                }}
              />
            </div>
          )}

          {/* Horário */}
          <button
            type="button"
            onClick={() => setPanel(panel === "time" ? "none" : "time")}
            className={cn("flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition", panel === "time" ? "ai-soft" : "bg-slate-50 dark:bg-slate-800/60")}
          >
            <Clock className="h-5 w-5 ai-text" />
            <span className="flex-1 text-lg font-extrabold tabular-nums text-slate-800 dark:text-slate-100">{time}</span>
            {past && <span className="text-xs font-semibold text-amber-600">já passou</span>}
          </button>
          {panel === "time" && <TimeWheel value={time} onChange={setTime} />}

          {/* Lembrete */}
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
              <Bell className="h-4 w-4 ai-text" /> Lembrete
            </div>
            <div className="flex flex-wrap gap-2">
              {REMINDERS.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => setRemind(r.value)}
                  aria-pressed={remind === r.value}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-[13px] font-semibold transition active:scale-95",
                    remind === r.value ? "ai-grad shadow-sm" : "border ai-border bg-white ai-text dark:bg-slate-800",
                  )}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Descrição */}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="Descrição (opcional) — local, pessoa, o que levar…"
            className="w-full resize-none rounded-2xl bg-slate-50 px-3 py-3 text-[15px] text-slate-800 outline-none focus:ring-2 focus:ring-[var(--ai)] dark:bg-slate-800/60 dark:text-slate-100"
          />

          {error && <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</p>}
        </div>
      </div>
    </div>
  );
}
